import { NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  FaHome,
  FaCrown,
  FaTrophy,
  FaUsers,
  FaMedal,
  FaFire,
  FaBolt,
  FaChartBar,
  FaShieldAlt,
  FaCheckCircle,
  FaBalanceScale,
  FaUser,
  FaSearch,
  FaExchangeAlt,
  FaHistory,
  FaLayerGroup,
  FaRandom,
  FaGlobe,
  FaPenNib,
  FaExternalLinkAlt,
  FaChevronDown,
  FaChevronRight,
  FaCalendarAlt,
  FaSkullCrossbones,
  FaFacebookF,
  FaInstagram,
} from "react-icons/fa";

const Sidebar = ({ isOpen, toggleSidebar, collapsed }) => {
  const location = useLocation();
  const [hovered, setHovered] = useState(false);
  const showLabels = !(collapsed && !hovered);
  const pathname = location.pathname;
  const search = location.search;

  const isS16Route =
    pathname === "/s16" ||
    pathname === "/captainmode" ||
    pathname === "/elimination" ||
    pathname.startsWith("/squad-rival") ||
    pathname.startsWith("/gauntlet");
  const isThe100Route = pathname === "/the-100";

  const [s16Open, setS16Open] = useState(() => {
    const saved = localStorage.getItem("fpl_sidebar_s16_open");
    return saved == null ? true : saved === "1";
  });
  const [the100Open, setThe100Open] = useState(() => localStorage.getItem("fpl_sidebar_the100_open") === "1");

  useEffect(() => {
    if (isS16Route) setS16Open(true);
    if (isThe100Route) setThe100Open(true);
  }, [isS16Route, isThe100Route]);

  useEffect(() => {
    localStorage.setItem("fpl_sidebar_s16_open", s16Open ? "1" : "0");
  }, [s16Open]);
  useEffect(() => {
    localStorage.setItem("fpl_sidebar_the100_open", the100Open ? "1" : "0");
  }, [the100Open]);

  const linkBase = "fpl-side-link";
  const navClass = ({ isActive }) => `${linkBase} ${isActive ? "is-active" : ""}`;
  const queryActive = (path, tab) => pathname === path && new URLSearchParams(search).get("tab") === tab;

  const groups = [
    {
      title: "Data & Tools",
      items: [
        ["/fpllive", "FPL Live", FaBolt],
        ["/clean-sheet", "Clean Sheet Chances", FaShieldAlt],
        ["/check-registration", "Check Registration", FaCheckCircle],
        ["/tieanalyzer", "Tie Analyzer", FaBalanceScale],
      ],
    },
    {
      title: "Team & Players",
      items: [
        ["/my-team", "My Team", FaUser],
        ["/players", "Players", FaSearch],
        ["/compare", "Compare", FaExchangeAlt],
        ["/history", "History", FaHistory],
        ["/squad-compare", "Squad Compare", FaLayerGroup],
        ["/versus", "Versus", FaRandom],
      ],
    },
  ];

  const renderSubLink = ({ to, label, Icon, active = false, external = false }) => {
    const classes = `fpl-side-link fpl-side-sublink ${active ? "is-active" : ""}`;
    if (external) {
      return (
        <a key={to} href={to} target="_blank" rel="noopener noreferrer" className={classes} title={`${label} (opens in a new tab)`}>
          <span className="fpl-side-icon"><Icon /></span>
          {showLabels && <span className="truncate">{label}</span>}
          {showLabels && <FaExternalLinkAlt className="ml-auto text-white/25 text-[9px]" />}
        </a>
      );
    }
    return (
      <NavLink key={to} to={to} className={classes} title={label}>
        <span className="fpl-side-icon"><Icon /></span>
        {showLabels && <span className="truncate">{label}</span>}
      </NavLink>
    );
  };

  return (
    <>
      <div
        onClick={toggleSidebar}
        className={`fixed inset-0 bg-slate-950/50 backdrop-blur-sm transition-opacity md:hidden z-40 ${
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden={!isOpen}
      />

      <aside
        aria-label="Sidebar"
        className={`fpl-sidebar fixed left-0 top-0 z-50 flex flex-col h-screen ${
          collapsed ? "md:w-20" : "md:w-[236px]"
        } w-72 transform transition-all duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        } md:translate-x-0`}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <div className="fpl-sidebar-brand">
          <button
            onClick={toggleSidebar}
            className="absolute right-4 top-4 md:hidden text-white/70 hover:text-white text-lg"
            aria-label="Close sidebar"
          >
            ×
          </button>

          <div className="flex items-center gap-3 min-w-0">
            <NavLink to="/" className="fpl-brand-logo-link" aria-label="FPL Nepal Home">
              <img src="/fpl-nepal-logo.png" alt="FPL Nepal" className="fpl-brand-logo" />
            </NavLink>
            {showLabels && (
              <div className="min-w-0 fpl-brand-season-copy">
                <div className="flex items-center gap-2">
                  <span className="fpl-season-pill">2026/27</span>
                  <span className="text-[9px] uppercase tracking-[0.18em] text-white/40">Season 16</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5 fpl-sidebar-scroll">
          <div>
            {showLabels && <p className="fpl-side-label">Home</p>}
            <NavLink to="/" end className={navClass} title="Home">
              <span className="fpl-side-icon"><FaHome /></span>
              {showLabels && <span>Home</span>}
            </NavLink>
          </div>

          <div>
            {showLabels && <p className="fpl-side-label">Competitions</p>}

            <div className={`fpl-side-parent ${isS16Route ? "is-current" : ""}`}>
              <button
                type="button"
                className="fpl-side-parent-btn"
                onClick={() => showLabels ? setS16Open((v) => !v) : null}
                title="S16"
                aria-expanded={s16Open}
              >
                <span className="fpl-side-icon"><FaTrophy /></span>
                {showLabels && <span className="truncate font-extrabold">S16</span>}
                {showLabels && (s16Open ? <FaChevronDown className="ml-auto text-[10px]" /> : <FaChevronRight className="ml-auto text-[10px]" />)}
              </button>
              {showLabels && s16Open && (
                <div className="fpl-side-submenu">
                  {renderSubLink({ to: "/s16?tab=standings", label: "Standings", Icon: FaTrophy, active: pathname === "/s16" && (!new URLSearchParams(search).get("tab") || queryActive("/s16", "standings")) })}
                  {renderSubLink({ to: "/s16?tab=gw", label: "GW Winners", Icon: FaMedal, active: queryActive("/s16", "gw") })}
                  {renderSubLink({ to: "/s16?tab=month", label: "Manager of the Month", Icon: FaCalendarAlt, active: queryActive("/s16", "month") })}
                  {renderSubLink({ to: "/s16?tab=stats", label: "League Stats", Icon: FaChartBar, active: queryActive("/s16", "stats") })}
                  {renderSubLink({ to: "/captainmode", label: "Captain Mode", Icon: FaCrown, active: pathname === "/captainmode" })}
                  {renderSubLink({ to: "/elimination", label: "Elimination", Icon: FaSkullCrossbones, active: pathname === "/elimination" })}
                  {renderSubLink({ to: "/squad-rival", label: "Squad Rival", Icon: FaUsers, active: pathname.startsWith("/squad-rival"), external: true })}
                  {renderSubLink({ to: "/gauntlet", label: "Gauntlet", Icon: FaFire, active: pathname.startsWith("/gauntlet"), external: true })}
                </div>
              )}
            </div>

            <div className={`fpl-side-parent mt-1 ${isThe100Route ? "is-current" : ""}`}>
              <button
                type="button"
                className="fpl-side-parent-btn"
                onClick={() => showLabels ? setThe100Open((v) => !v) : null}
                title="THE 100"
                aria-expanded={the100Open}
              >
                <span className="fpl-side-icon"><FaUsers /></span>
                {showLabels && <span className="truncate font-extrabold">THE 100</span>}
                {showLabels && (the100Open ? <FaChevronDown className="ml-auto text-[10px]" /> : <FaChevronRight className="ml-auto text-[10px]" />)}
              </button>
              {showLabels && the100Open && (
                <div className="fpl-side-submenu">
                  {renderSubLink({ to: "/the-100?tab=standings", label: "Standings", Icon: FaTrophy, active: pathname === "/the-100" && (!new URLSearchParams(search).get("tab") || queryActive("/the-100", "standings")) })}
                  {renderSubLink({ to: "/the-100?tab=gw", label: "GW Winner", Icon: FaMedal, active: queryActive("/the-100", "gw") })}
                  {renderSubLink({ to: "/the-100?tab=stats", label: "League Stats", Icon: FaChartBar, active: queryActive("/the-100", "stats") })}
                </div>
              )}
            </div>
          </div>

          {groups.map((group) => (
            <div key={group.title}>
              {showLabels && <p className="fpl-side-label">{group.title}</p>}
              <div className="space-y-1">
                {group.items.map(([to, label, Icon, end]) => (
                  <NavLink key={to} to={to} end={Boolean(end)} className={navClass} title={label}>
                    <span className="fpl-side-icon"><Icon /></span>
                    {showLabels && <span className="truncate">{label}</span>}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}

          <div>
            {showLabels && <p className="fpl-side-label">Community</p>}
            <a href="https://www.fplnepal.com/" target="_blank" rel="noopener noreferrer" className={linkBase}>
              <span className="fpl-side-icon"><FaGlobe /></span>
              {showLabels && <span>Website</span>}
              {showLabels && <FaExternalLinkAlt className="ml-auto text-white/25 text-[9px]" />}
            </a>
            <a href="https://blog.fplnepal.com/" target="_blank" rel="noopener noreferrer" className={linkBase}>
              <span className="fpl-side-icon"><FaPenNib /></span>
              {showLabels && <span>Blog</span>}
              {showLabels && <FaExternalLinkAlt className="ml-auto text-white/25 text-[9px]" />}
            </a>
            <a href="https://www.facebook.com/groups/FPLNepal" target="_blank" rel="noopener noreferrer" className={linkBase}>
              <span className="fpl-side-icon"><FaFacebookF /></span>
              {showLabels && <span>Facebook Group</span>}
              {showLabels && <FaExternalLinkAlt className="ml-auto text-white/25 text-[9px]" />}
            </a>
            <a href="https://www.facebook.com/gaming/FPLNPL" target="_blank" rel="noopener noreferrer" className={linkBase}>
              <span className="fpl-side-icon"><FaFacebookF /></span>
              {showLabels && <span>Facebook Page</span>}
              {showLabels && <FaExternalLinkAlt className="ml-auto text-white/25 text-[9px]" />}
            </a>
            <a href="https://www.instagram.com/fpl.nepal/" target="_blank" rel="noopener noreferrer" className={linkBase}>
              <span className="fpl-side-icon"><FaInstagram /></span>
              {showLabels && <span>Instagram</span>}
              {showLabels && <FaExternalLinkAlt className="ml-auto text-white/25 text-[9px]" />}
            </a>
            <a href="https://fplpeek.com/" target="_blank" rel="noopener noreferrer" className={linkBase}>
              <span className="fpl-side-icon"><FaChartBar /></span>
              {showLabels && <span>FPLPEEK</span>}
              {showLabels && <FaExternalLinkAlt className="ml-auto text-white/25 text-[9px]" />}
            </a>
          </div>
        </nav>

        <div className="fpl-sidebar-footer">
          {showLabels ? (
            <>
              <span className="fpl-footer-dot" />
              <span>FPL Nepal · 2026/27</span>
            </>
          ) : "26/27"}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
